import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Database, RefreshCw, Upload, CheckCircle, AlertCircle, Clock,
  HardDrive, FileUp, Trash2, Download, ChevronDown, ChevronUp,
  DollarSign, Settings, Package, Users,
  ClipboardList, Shield, Layers, GitBranch,
} from 'lucide-react';
import DataLinkageMap from '@/components/DataLinkageMap';

const CHUNK_SIZE = 5 * 1024 * 1024;

// Collection metadata for grouped display
const COLLECTION_GROUPS = {
  'Core CRM Data': {
    icon: Users,
    color: '#D35400',
    collections: {
      clients: { label: 'Clients', accessTable: 'ClientDetailsTbl / Clients' },
      properties: { label: 'Properties', accessTable: 'PropertyDetails / Properties' },
      appraisals: { label: 'Appraisals', accessTable: 'AppraisalTbl / Appraisals' },
      leads: { label: 'Leads', accessTable: 'LeadTable / Leads' },
    }
  },
  'Job & Inspection': {
    icon: ClipboardList,
    color: '#0284C7',
    collections: {
      quote_items: { label: 'Quote Items', accessTable: 'temp1 / QuoteItems' },
      appraisal_components: { label: 'Components', accessTable: 'AppraisalComponents' },
      work_sheets: { label: 'Work Sheets', accessTable: 'WorkSheetDaysTbl / WorkSheets' },
      roof_details: { label: 'Roof Details', accessTable: 'RoofDetails' },
      contractors: { label: 'Contractors', accessTable: 'ContractorDetailsTbl / Contractors' },
    }
  },
  'Financial': {
    icon: DollarSign,
    color: '#16A34A',
    collections: {
      invoices: { label: 'Invoices', accessTable: 'Invoices + InvoiceLineItems' },
      expenses: { label: 'Expenses', accessTable: 'Expenses' },
    }
  },
  'Suppliers & Products': {
    icon: Package,
    color: '#7C3AED',
    collections: {
      suppliers: { label: 'Suppliers', accessTable: 'Suppliers' },
      product_catalog: { label: 'Product Catalog', accessTable: 'ProductCatalog' },
    }
  },
  'Insurance': {
    icon: Shield,
    color: '#F59E0B',
    collections: {
      insurance_companies: { label: 'Insurance Cos.', accessTable: 'InsuranceCompanies' },
      insurance_assessors: { label: 'Assessors', accessTable: 'InsuranceAssessors' },
    }
  },
  'Configuration': {
    icon: Settings,
    color: '#64748B',
    collections: {
      gst_rates: { label: 'GST Rates', accessTable: 'GSTRates' },
      estimate_rates: { label: 'Estimate Rates', accessTable: 'EstimateRates' },
      roof_conditions: { label: 'Roof Conditions', accessTable: 'RoofConditions' },
      report_settings: { label: 'Report Settings', accessTable: 'ReportSettings' },
      dropdown_options: { label: 'Dropdown Options', accessTable: 'DropdownOptions' },
    }
  },
};

export default function DataSyncPage() {
  const { api } = useAuth();
  const [syncStatus, setSyncStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState('');
  const [exportStats, setExportStats] = useState(null);
  const [mdbStatus, setMdbStatus] = useState(null);
  const [generatingMdb, setGeneratingMdb] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ import: false, sync: false });
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const mdbPollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/sync/status');
      setSyncStatus(data);
    } catch { /* ignore */ }
  }, [api]);

  const loadFiles = useCallback(async () => {
    try {
      const { data } = await api.get('/sync/files');
      setFiles(data.files || []);
      if (data.files?.length && !selectedFile) {
        setSelectedFile(data.files[0].path);
      }
    } catch { /* ignore */ }
  }, [api, selectedFile]);

  const loadExportStats = useCallback(async () => {
    try {
      const { data } = await api.get('/sync/export-stats');
      setExportStats(data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => {
    loadStatus();
    loadFiles();
    loadExportStats();
  }, [loadStatus, loadFiles, loadExportStats]);

  useEffect(() => {
    if (syncStatus?.running) {
      pollRef.current = setInterval(loadStatus, 2000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [syncStatus?.running, loadStatus]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mdb')) {
      setError('Only .mdb files are supported');
      return;
    }
    setError('');
    setUploading(true);
    setUploadProgress(0);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('file', chunk, file.name);
        formData.append('chunk_index', i);
        formData.append('total_chunks', totalChunks);
        formData.append('filename', file.name);
        await api.post('/sync/upload-chunk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      await loadFiles();
      setUploadProgress(100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startSync = async () => {
    if (!selectedFile) { setError('Select a .mdb file first'); return; }
    setError('');
    try {
      await api.post('/sync/execute', { mdb_path: selectedFile });
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start sync');
    }
  };

  const cleanFakeData = async () => {
    if (!window.confirm('Remove all non-imported (fake/test) data? This cannot be undone.')) return;
    try {
      const { data } = await api.post('/sync/clean-fake-data');
      alert(`Cleanup complete. Removed: ${JSON.stringify(data.removed, null, 2)}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleExport = (format) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const endpoint = format === 'modern' ? '/api/sync/export-modern' : '/api/sync/export';
    window.open(`${baseUrl}${endpoint}`, '_blank');
  };

  // MDB generation
  const loadMdbStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/sync/mdb-status');
      setMdbStatus(data);
      if (!data.running && generatingMdb) setGeneratingMdb(false);
    } catch { /* ignore */ }
  }, [api, generatingMdb]);

  useEffect(() => { loadMdbStatus(); }, [loadMdbStatus]);

  useEffect(() => {
    if (generatingMdb) {
      mdbPollRef.current = setInterval(loadMdbStatus, 2000);
    } else {
      clearInterval(mdbPollRef.current);
    }
    return () => clearInterval(mdbPollRef.current);
  }, [generatingMdb, loadMdbStatus]);

  const handleGenerateMdb = async () => {
    setGeneratingMdb(true);
    setError('');
    try {
      await api.post('/sync/generate-mdb');
      loadMdbStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start .mdb generation');
      setGeneratingMdb(false);
    }
  };

  const handleDownloadMdb = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${baseUrl}/api/sync/download-mdb`, '_blank');
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalRecords = exportStats ? Object.values(exportStats).reduce((a, b) => a + b, 0) : 0;
  const isRunning = syncStatus?.running;
  const results = syncStatus?.results || {};
  const finalCounts = results.final_counts || {};

  return (
    <div data-testid="data-sync-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Data Sync & Export</h1>
          <p className="text-sm text-slate-500 mt-1">Full 2-way sync between CRM and MS Access. Export all {totalRecords.toLocaleString()} records across {exportStats ? Object.keys(exportStats).length : 0} tables.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="refresh-stats-btn" onClick={() => { loadExportStats(); loadStatus(); loadFiles(); }} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="clean-fake-data-btn" onClick={cleanFakeData} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-100 transition-colors">
            <Trash2 size={14} /> Clean Test Data
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="error-banner" className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Data Linkage Map section — same component as Settings page,
          embedded here so the operator can spot orphan/missing links
          while reviewing the sync state. */}
      <details className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="data-sync-linkage-section">
        <summary className="px-6 py-4 cursor-pointer text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
          <GitBranch size={16} className="text-[#D35400]" />
          Data Linkage Map
          <span className="text-xs text-slate-400 italic font-normal ml-2">how entities link + where info is missing</span>
        </summary>
        <div className="p-4 border-t border-slate-100">
          <DataLinkageMap />
        </div>
      </details>

      {/* === CRM DATA OVERVIEW === */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-[#D35400]" />
            <h2 className="text-base font-semibold text-[#0F172A]">CRM Data Overview</h2>
          </div>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full font-medium">
            {totalRecords.toLocaleString()} total records
          </span>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(COLLECTION_GROUPS).map(([groupName, group]) => {
              const GroupIcon = group.icon;
              const groupTotal = Object.keys(group.collections).reduce((sum, col) => sum + (exportStats?.[col] || 0), 0);
              return (
                <div key={groupName} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: group.color + '15' }}>
                      <GroupIcon size={14} style={{ color: group.color }} />
                    </div>
                    <h3 className="text-sm font-semibold text-[#0F172A]">{groupName}</h3>
                    <span className="ml-auto text-xs font-mono font-bold" style={{ color: group.color }}>{groupTotal.toLocaleString()}</span>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(group.collections).map(([colKey, colMeta]) => (
                      <div key={colKey} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{colMeta.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 text-[10px] truncate max-w-[120px]" title={colMeta.accessTable}>{colMeta.accessTable}</span>
                          <span className="font-mono font-bold text-slate-700 w-12 text-right">{(exportStats?.[colKey] || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* === EXPORT SECTION === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Modern Export */}
        <div className="bg-white border-2 border-[#16A34A] rounded-xl p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <Database size={18} className="text-[#16A34A]" />
            <h3 className="text-sm font-bold text-[#0F172A]">Export to Modern Access Database</h3>
          </div>
          <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-[#16A34A] bg-green-50 px-2 py-0.5 rounded-full mb-2 w-fit">CSV + Blueprint</span>
          <p className="text-xs text-slate-600 mb-3 flex-1">Complete blueprint: 20 data tables, 12 form layouts, 8 reports with SQL, VBA code modules, status normalization, and auto-import macro.</p>
          <button data-testid="export-modern-btn" onClick={() => handleExport('modern')} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#16A34A] text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors w-full">
            <Download size={14} /> Export Modern DB (ZIP)
          </button>
        </div>

        {/* MDB Download */}
        <div className="bg-white border-2 border-[#D35400] rounded-xl p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive size={18} className="text-[#D35400]" />
            <h3 className="text-sm font-bold text-[#0F172A]">Create Access .mdb File</h3>
          </div>
          <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-[#D35400] bg-orange-50 px-2 py-0.5 rounded-full mb-2 w-fit">Direct Open</span>
          <p className="text-xs text-slate-600 mb-2 flex-1">Generate a complete .mdb file ready to open in Microsoft Access. Includes all tables and data.</p>
          {mdbStatus?.progress && (
            <div data-testid="mdb-progress" className={`text-xs px-2 py-1.5 rounded mb-2 ${generatingMdb ? 'bg-orange-50 text-[#D35400]' : mdbStatus.error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
              {mdbStatus.error || mdbStatus.progress}
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="generate-mdb-btn" onClick={handleGenerateMdb} disabled={generatingMdb} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50 transition-colors">
              {generatingMdb ? <RefreshCw size={14} className="animate-spin" /> : <Database size={14} />}
              {generatingMdb ? 'Building...' : 'Generate'}
            </button>
            <button data-testid="download-mdb-btn" onClick={handleDownloadMdb} disabled={generatingMdb} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
              <Download size={14} /> Download
            </button>
          </div>
        </div>
      </div>

      {/* === IMPORT SECTION (Collapsible) === */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button
          data-testid="toggle-import-section"
          onClick={() => toggleSection('import')}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-[#D35400]" />
            <h2 className="text-base font-semibold text-[#0F172A]">Import from Access</h2>
            <span className="text-xs text-slate-500">Upload .mdb and sync into CRM</span>
          </div>
          {expandedSections.import ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </button>

        {expandedSections.import && (
          <div className="px-6 pb-5 space-y-4 border-t border-slate-100 pt-4">
            {/* Upload */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Upload .mdb File</label>
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mdb"
                  onChange={handleFileUpload}
                  disabled={uploading || isRunning}
                  className="block text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#D35400] file:text-white hover:file:bg-[#B84500] file:cursor-pointer disabled:opacity-50"
                  data-testid="mdb-file-input"
                />
                {uploading && (
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D35400] transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <span className="text-xs text-slate-500">{uploadProgress}%</span>
                  </div>
                )}
              </div>
            </div>

            {/* Available Files */}
            {files.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Available Files</label>
                <div className="space-y-2">
                  {files.map((f, i) => (
                    <label key={i} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedFile === f.path ? 'border-[#D35400] bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input type="radio" name="mdbFile" value={f.path} checked={selectedFile === f.path} onChange={() => setSelectedFile(f.path)} className="accent-[#D35400]" data-testid={`file-radio-${i}`} />
                      <Database size={14} className="text-slate-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#0F172A]">{f.name}</div>
                        <div className="text-xs text-slate-400">{f.size_mb} MB &middot; {new Date(f.modified).toLocaleDateString()}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Sync Controls */}
            <div className="flex gap-3">
              <button data-testid="start-sync-btn" onClick={startSync} disabled={isRunning || !selectedFile} className="flex items-center gap-2 px-5 py-2.5 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {isRunning ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                {isRunning ? 'Syncing...' : 'Start Sync'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* === SYNC RESULTS (Collapsible) === */}
      {syncStatus && (syncStatus.started_at || isRunning) && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            data-testid="toggle-sync-section"
            onClick={() => toggleSection('sync')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              {isRunning ? (
                <RefreshCw size={18} className="text-[#D35400] animate-spin" />
              ) : syncStatus.error ? (
                <AlertCircle size={18} className="text-red-500" />
              ) : (
                <CheckCircle size={18} className="text-green-600" />
              )}
              <h2 className="text-base font-semibold text-[#0F172A]">
                {isRunning ? 'Sync In Progress' : syncStatus.error ? 'Last Sync (Failed)' : 'Last Sync Results'}
              </h2>
            </div>
            {expandedSections.sync ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>

          {(expandedSections.sync || isRunning) && (
            <div className="px-6 pb-5 space-y-4 border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock size={14} className="text-slate-400" />
                <span className="text-slate-500">
                  {syncStatus.started_at && `Started: ${new Date(syncStatus.started_at).toLocaleString()}`}
                  {syncStatus.completed_at && ` | Completed: ${new Date(syncStatus.completed_at).toLocaleString()}`}
                </span>
              </div>

              {syncStatus.progress && (
                <div data-testid="sync-progress" className={`text-sm px-3 py-2 rounded-lg ${isRunning ? 'bg-orange-50 text-[#D35400]' : syncStatus.error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                  {syncStatus.progress}
                </div>
              )}

              {syncStatus.error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{syncStatus.error}</div>
              )}

              {Object.keys(results).length > 0 && !isRunning && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(results).filter(([k]) => k !== 'final_counts' && k !== 'total_contract_value').map(([collection, stats]) => (
                      <div key={collection} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="text-xs font-medium text-[#D35400] uppercase tracking-wide">{collection.replace(/_/g, ' ')}</div>
                        <div className="mt-1 text-xs text-slate-600">
                          {Object.entries(stats).map(([k, v]) => (
                            <div key={k}><span className="text-slate-400">{k}:</span> <span className="font-medium">{typeof v === 'number' ? v.toLocaleString() : v}</span></div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {Object.keys(finalCounts).length > 0 && (
                    <>
                      <h3 className="text-sm font-semibold text-[#0F172A]">Database Totals</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {Object.entries(finalCounts).map(([col, count]) => (
                          <div key={col} className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                            <div className="text-lg font-bold text-green-700">{count.toLocaleString()}</div>
                            <div className="text-xs text-green-600 mt-0.5">{col.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3">How Data Sync Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-600">
          <div>
            <h3 className="font-semibold text-slate-800 mb-1.5">Import (Access &rarr; CRM)</h3>
            <ol className="space-y-1 list-decimal list-inside">
              <li>Upload your .mdb file (chunked upload, supports 1GB+)</li>
              <li>System extracts all tables using mdbtools</li>
              <li>Existing records (by Access ID) are updated; new ones inserted</li>
              <li>Cross-references (Client &rarr; Property &rarr; Appraisal) maintained</li>
            </ol>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 mb-1.5">Export (CRM &rarr; Access)</h3>
            <ol className="space-y-1 list-decimal list-inside">
              <li>Downloads all 20 tables as CSV files in a ZIP</li>
              <li>Includes VBA code, form specs, report SQL, and auto-import macro</li>
              <li>.mdb generation creates a ready-to-open Access database file</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
